import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { format } from 'date-fns';
import { numberFormat } from 'highcharts';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CompanyService } from 'src/app/root/company.service';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

@Component({
  selector: 'lohi-shift-averages',
  templateUrl: './shift-averages.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftAveragesComponent implements OnInit, OnDestroy {
  private destroy$$ = new Subject();
  public dateFormat = 'M/dd/yyyy';
  public labels: Array<string>;

  public chart: Chart;

  constructor(private companyService: CompanyService, private cd: ChangeDetectorRef) {}

  public ngOnInit() {
    this.companyService.companyShiftAverages$.pipe(takeUntil(this.destroy$$)).subscribe(async (data) => {
      if (!data || !data?.entries.length) {
        return;
      }
      const labels = data.entries.map((entry) =>
        dateFormat(new Date(entry.year, entry.month - 1, entry.day, 0, 0, 0, 0).toISOString()),
      );
      const metrics = await this.companyService.companyMetrics30Day$
        .pipe(
          filter((m) => !!m),
          take(1),
        )
        .toPromise();
      this.chart = new Chart({
        chart: {
          type: 'column',
        },
        title: {
          align: 'left',
          useHTML: true,
          text: `<div class="text-lg font-bold">Revenue Per Shift <span class="text-blue-500">${
            metrics.daysOfHistory || 30
          }d</span></div>`,
        },
        subtitle: data.trend
          ? {
              align: 'left',
              useHTML: true,
              text: `<div class="text-gray-700">Your revenue is ${data.trend === 'flat' ? '' : 'going '}${
                data.trend
              }.</div>`,
            }
          : null,
        credits: {
          enabled: false,
        },
        yAxis: [
          {
            labels: {
              enabled: false,
            },
            tickInterval: 0,
            title: { text: null },
          },
          {
            title: { text: 'Dollars' },
          },
        ],
        plotOptions: {
          series: {
            stacking: 'normal',
          }
        },
        xAxis: {
          type: 'category',
          categories: labels,
          visible: true,
        },
        series: [],
      });
      const totalEarnings: Array<[string, number]> = [];
      const totalMissed: Array<[string, number]> = [];
      const turnRate: Array<[string, number]> = [];
      for (const entry of data.entries) {
        const dateStr = dateFormat(new Date(entry.year, entry.month - 1, entry.day, 0, 0, 0, 0).toISOString());
        totalEarnings.push([dateStr, entry.averageEarnings / 100]);
        totalMissed.push([dateStr, entry.averageMissedEarnings / 100]);
        if (entry.averageTurnRate > 0) {
          turnRate.push([dateStr, entry.averageTurnRate]);
        }
      }
      this.chart.addSeries(
        {
          name: 'Average Missed Revenue Per Shift',
          type: 'column',
          color: '#EF6048',
          data: totalMissed,
          yAxis: 1,
          tooltip: {
            pointFormatter() {
              return (
                `<span class="font-bold" style="color: ${this.color}">` +
                this.series.name +
                '</span> ' +
                currencyFormatter.format(this.y)
              );
            },
          },
        },
        true,
        true,
      );
      this.chart.addSeries(
        {
          name: 'Average Revenue Per Shift',
          type: 'column',
          color: {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: [
              [0, '#070707'],
              [1, '#3e3e52'],
            ],
          },
          data: totalEarnings,
          yAxis: 1,
          tooltip: {
            pointFormatter() {
              return (
                `<span class="font-bold" style="color: ${this.color}">` +
                this.series.name +
                '</span> ' +
                currencyFormatter.format(this.y)
              );
            },
          },
        },
        true,
        true,
      );
      this.chart.addSeries(
        {
          name: 'Average Turn Rate',
          type: 'spline',
          dashStyle: 'Dash',
          color: '#448AFF',
          data: turnRate,
          tooltip: {
            pointFormatter() {
              return (
                `<span class="font-bold" style="color: ${this.color}">` +
                this.series.name +
                '</span> ' +
                numberFormat(this.y, 2)
              );
            },
          },
        },
        true,
        true,
      );
      this.cd.markForCheck();
    });
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }
}

function dateFormat(date: string): string {
  return format(new Date(date), 'M/dd/yyyy', {
    useAdditionalDayOfYearTokens: true,
    useAdditionalWeekYearTokens: true,
  });
}
